import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  CheckCircle,
  XCircle,
  Save,
  Edit,
} from "lucide-react";
import { supabase } from "../../integrations/supabase/client";
import { toast } from "sonner";

interface Institution {
  id: string;
  name: string;
  type: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  address_line1: string | null;
  address_line2: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  status: string | null;
  created_at: string;
  updated_at: string;
  total_students: number | null;
  staff_count: number | null;
  trustee_name: string | null;
  trustee_contact: string | null;
  departments: string[] | null;
}

const steps = [
  "Type of Institution",
  "Basic Details",
  "Address Details",
  "Capacity & Staff",
  "Trustee Details",
  "Review & Action",
];

export default function InstitutionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = usePrefixedNavigate();
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [editForm, setEditForm] = useState<Partial<Institution>>({});
  const [actionReason, setActionReason] = useState("");

  const fetchInstitution = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const { data, error } = await supabase
        .from("institutions")
        .select("*")
        .eq("id", id as string)
        .single();

      if (error) {
        throw error;
      }
      setInstitution(data as Institution);
      setEditForm(data as Partial<Institution>);
    } catch (error: any) {
      console.error("Error fetching institution:", error);
      toast.error("Failed to fetch institution details");
      navigate("/super-admin/institutions");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchInstitution();
    }
  }, [id, fetchInstitution]);

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    if (!institution) {
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("institutions")
        .update({
          name: editForm.name,
          type: editForm.type,
          city: editForm.city,
          state: editForm.state,
          pincode: editForm.pincode,
          address_line1: editForm.address_line1,
          address_line2: editForm.address_line2,
          email: editForm.email,
          phone: editForm.phone,
          website: editForm.website,
          total_students: editForm.total_students,
          staff_count: editForm.staff_count,
          trustee_name: editForm.trustee_name,
          trustee_contact: editForm.trustee_contact,
          departments: editForm.departments,
        })
        .eq("id", institution.id);

      if (error) {
        throw error;
      }

      toast.success("Institution updated successfully");
      setIsEditing(false);
      fetchInstitution();
    } catch (error: any) {
      console.error("Error updating institution:", error);
      toast.error("Failed to update institution");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: "approved" | "rejected") => {
    if (!institution) {
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("institutions")
        .update({ status: newStatus })
        .eq("id", institution.id);

      if (error) {
        throw error;
      }

      toast.success(
        `Institution ${newStatus === "approved" ? "approved" : "rejected"} successfully`
      );
      setActionReason("");
      fetchInstitution();
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error("Failed to update institution status");
    } finally {
      setSaving(false);
    }
  };

  const getStatusConfig = (status: string | null) => {
    switch (status) {
      case "approved":
        return {
          icon: CheckCircle,
          label: "Approved",
          className: "bg-green-100 text-green-800 border-green-300",
          iconClass: "text-green-600",
        };
      case "rejected":
        return {
          icon: XCircle,
          label: "Rejected",
          className: "bg-red-100 text-red-800 border-red-300",
          iconClass: "text-red-600",
        };
      default:
        return {
          icon: Clock,
          label: "Pending Review",
          className: "bg-yellow-100 text-yellow-800 border-yellow-300",
          iconClass: "text-yellow-600",
        };
    }
  };

  const updateFormData = (field: keyof Institution, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-6">
        <div className="space-y-3 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-muted-foreground">
            Loading institution details...
          </p>
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Institution not found</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(institution.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/super-admin/institutions")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-foreground">
            Review Institution
          </h1>
          <p className="text-muted-foreground">
            Step {currentStep} of 6: {steps[currentStep - 1]}
          </p>
        </div>
        <Badge
          className={`${statusConfig.className} px-3 py-1.5 text-sm font-medium`}
        >
          <StatusIcon className={`mr-1.5 h-4 w-4 ${statusConfig.iconClass}`} />
          {statusConfig.label}
        </Badge>
        {!isEditing ? (
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Mode
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setEditForm(institution);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </div>

      {/* Progress Stepper */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step} className="flex flex-1 items-center">
            <div className="flex flex-col items-center">
              <button
                onClick={() => setCurrentStep(index + 1)}
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                  index + 1 <= currentStep
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted bg-background hover:border-primary/50"
                }`}
              >
                {index + 1 < currentStep ? (
                  <Check className="h-5 w-5" />
                ) : (
                  index + 1
                )}
              </button>
              <span className="mt-2 max-w-[80px] text-center text-xs">
                {step}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`mx-2 h-0.5 flex-1 ${index + 1 < currentStep ? "bg-primary" : "bg-muted"}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStep - 1]}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Type of Institution */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Type of Institution</Label>
                <Select
                  value={editForm.type ?? ""}
                  onValueChange={value => updateFormData("type", value)}
                  disabled={!isEditing}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="university">University</SelectItem>
                    <SelectItem value="polytechnic">Polytechnic</SelectItem>
                    <SelectItem value="iti">ITI</SelectItem>
                    <SelectItem value="coaching">Coaching Center</SelectItem>
                    <SelectItem value="gurukul">Gurukul</SelectItem>
                    <SelectItem value="pathshala">Pathshala</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Basic Details */}
          {currentStep === 2 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Institution Name</Label>
                <Input
                  value={editForm.name ?? ""}
                  onChange={e => updateFormData("name", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email ?? ""}
                  onChange={e => updateFormData("email", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={editForm.phone ?? ""}
                  onChange={e => updateFormData("phone", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Website</Label>
                <Input
                  value={editForm.website ?? ""}
                  onChange={e => updateFormData("website", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}

          {/* Step 3: Address Details */}
          {currentStep === 3 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Address Line 1</Label>
                <Input
                  value={editForm.address_line1 ?? ""}
                  onChange={e =>
                    updateFormData("address_line1", e.target.value)
                  }
                  disabled={!isEditing}
                  className="mt-1.5"
                  placeholder="Building name, Street address"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address Line 2</Label>
                <Input
                  value={editForm.address_line2 ?? ""}
                  onChange={e =>
                    updateFormData("address_line2", e.target.value)
                  }
                  disabled={!isEditing}
                  className="mt-1.5"
                  placeholder="Area, Landmark (optional)"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={editForm.city ?? ""}
                  onChange={e => updateFormData("city", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select
                  value={editForm.state ?? ""}
                  onValueChange={value => updateFormData("state", value)}
                  disabled={!isEditing}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      "Andhra Pradesh",
                      "Arunachal Pradesh",
                      "Assam",
                      "Bihar",
                      "Chhattisgarh",
                      "Goa",
                      "Gujarat",
                      "Haryana",
                      "Himachal Pradesh",
                      "Jharkhand",
                      "Karnataka",
                      "Kerala",
                      "Madhya Pradesh",
                      "Maharashtra",
                      "Manipur",
                      "Meghalaya",
                      "Mizoram",
                      "Nagaland",
                      "Odisha",
                      "Punjab",
                      "Rajasthan",
                      "Sikkim",
                      "Tamil Nadu",
                      "Telangana",
                      "Tripura",
                      "Uttar Pradesh",
                      "Uttarakhand",
                      "West Bengal",
                      "Delhi",
                      "Jammu and Kashmir",
                      "Ladakh",
                    ].map(state => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pincode</Label>
                <Input
                  value={editForm.pincode ?? ""}
                  onChange={e => updateFormData("pincode", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                  maxLength={6}
                />
              </div>
            </div>
          )}

          {/* Step 4: Capacity & Staff */}
          {currentStep === 4 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Total Students</Label>
                <Input
                  type="number"
                  value={editForm.total_students ?? ""}
                  onChange={e =>
                    updateFormData(
                      "total_students",
                      parseInt(e.target.value) || null
                    )
                  }
                  disabled={!isEditing}
                  className="mt-1.5"
                  min={0}
                />
              </div>
              <div>
                <Label>Staff Count</Label>
                <Input
                  type="number"
                  value={editForm.staff_count ?? ""}
                  onChange={e =>
                    updateFormData(
                      "staff_count",
                      parseInt(e.target.value) || null
                    )
                  }
                  disabled={!isEditing}
                  className="mt-1.5"
                  min={0}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Departments</Label>
                <Input
                  value={editForm.departments?.join(", ") ?? ""}
                  onChange={e =>
                    updateFormData(
                      "departments",
                      e.target.value
                        .split(",")
                        .map(d => d.trim())
                        .filter(Boolean)
                    )
                  }
                  disabled={!isEditing}
                  className="mt-1.5"
                  placeholder="Science, Arts, Commerce (comma separated)"
                />
              </div>
            </div>
          )}

          {/* Step 5: Trustee Details */}
          {currentStep === 5 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Trustee Name</Label>
                <Input
                  value={editForm.trustee_name ?? ""}
                  onChange={e => updateFormData("trustee_name", e.target.value)}
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Trustee Contact Number</Label>
                <Input
                  value={editForm.trustee_contact ?? ""}
                  onChange={e =>
                    updateFormData("trustee_contact", e.target.value)
                  }
                  disabled={!isEditing}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}

          {/* Step 6: Review & Action */}
          {currentStep === 6 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 gap-4 rounded-lg bg-muted/50 p-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Institution Name
                  </p>
                  <p className="font-medium">{institution.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">
                    {institution.type ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">
                    {institution.city}, {institution.state}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">
                    {institution.email ?? institution.phone ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Students
                  </p>
                  <p className="font-medium">
                    {institution.total_students ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Staff Count</p>
                  <p className="font-medium">{institution.staff_count ?? 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Trustee</p>
                  <p className="font-medium">
                    {institution.trustee_name ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Registered On</p>
                  <p className="font-medium">
                    {new Date(institution.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Action Section */}
              {institution.status === "pending" && (
                <div className="space-y-4 rounded-lg border border-yellow-200 bg-yellow-50/50 p-4">
                  <h3 className="flex items-center gap-2 font-semibold">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    Pending Approval
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Review all details above and take appropriate action.
                  </p>
                  <div>
                    <Label>Notes / Reason (Optional)</Label>
                    <Textarea
                      value={actionReason}
                      onChange={e => setActionReason(e.target.value)}
                      placeholder="Add any notes for approval or rejection reason..."
                      className="mt-1.5"
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleStatusChange("approved")}
                      disabled={saving}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {saving ? "Processing..." : "Approve Institution"}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleStatusChange("rejected")}
                      disabled={saving}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      {saving ? "Processing..." : "Reject Institution"}
                    </Button>
                  </div>
                </div>
              )}

              {institution.status === "approved" && (
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-4">
                  <h3 className="flex items-center gap-2 font-semibold text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    This institution has been approved
                  </h3>
                </div>
              )}

              {institution.status === "rejected" && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-4">
                  <h3 className="flex items-center gap-2 font-semibold text-red-700">
                    <XCircle className="h-5 w-5" />
                    This institution has been rejected
                  </h3>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {currentStep < 6 && (
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
